import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Square, Volume2, VolumeX, SkipBack, SkipForward, BookOpen, Download, Globe, ChevronDown, ChevronUp, Share2, Copy, Check, MessageCircle, Send, Languages, Mic } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

// Supported Languages with their codes - Major Indian & International Languages
const LANGUAGES = [
  // Indian Languages
  { code: 'hi-IN', name: 'हिंदी (Hindi)', flag: '🇮🇳', region: 'Indian' },
  { code: 'en-IN', name: 'English (India)', flag: '🇮🇳', region: 'Indian' },
  { code: 'ta-IN', name: 'தமிழ் (Tamil)', flag: '🇮🇳', region: 'Indian' },
  { code: 'te-IN', name: 'తెలుగు (Telugu)', flag: '🇮🇳', region: 'Indian' },
  { code: 'mr-IN', name: 'मराठी (Marathi)', flag: '🇮🇳', region: 'Indian' },
  { code: 'bn-IN', name: 'বাংলা (Bengali)', flag: '🇮🇳', region: 'Indian' },
  { code: 'gu-IN', name: 'ગુજરાતી (Gujarati)', flag: '🇮🇳', region: 'Indian' },
  { code: 'kn-IN', name: 'ಕನ್ನಡ (Kannada)', flag: '🇮🇳', region: 'Indian' },
  { code: 'ml-IN', name: 'മലയാളം (Malayalam)', flag: '🇮🇳', region: 'Indian' },
  { code: 'pa-IN', name: 'ਪੰਜਾਬੀ (Punjabi)', flag: '🇮🇳', region: 'Indian' },
  { code: 'or-IN', name: 'ଓଡ଼ିଆ (Odia)', flag: '🇮🇳', region: 'Indian' },
  { code: 'as-IN', name: 'অসমীয়া (Assamese)', flag: '🇮🇳', region: 'Indian' },
  { code: 'ur-IN', name: 'اردو (Urdu)', flag: '🇮🇳', region: 'Indian' },
  { code: 'ne-NP', name: 'नेपाली (Nepali)', flag: '🇳🇵', region: 'Indian' },
  // International Languages
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸', region: 'International' },
  { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧', region: 'International' },
  { code: 'es-ES', name: 'Español (Spanish)', flag: '🇪🇸', region: 'International' },
  { code: 'fr-FR', name: 'Français (French)', flag: '🇫🇷', region: 'International' },
  { code: 'de-DE', name: 'Deutsch (German)', flag: '🇩🇪', region: 'International' },
  { code: 'pt-BR', name: 'Português (Portuguese)', flag: '🇧🇷', region: 'International' },
  { code: 'ru-RU', name: 'Русский (Russian)', flag: '🇷🇺', region: 'International' },
  { code: 'ar-SA', name: 'العربية (Arabic)', flag: '🇸🇦', region: 'International' },
  { code: 'zh-CN', name: '中文 (Chinese)', flag: '🇨🇳', region: 'International' },
  { code: 'ja-JP', name: '日本語 (Japanese)', flag: '🇯🇵', region: 'International' },
  { code: 'ko-KR', name: '한국어 (Korean)', flag: '🇰🇷', region: 'International' },
];

// Guide Content - Chapters
const GUIDE_CHAPTERS = [
  {
    id: 1,
    title: 'Getting Started',
    content: `Welcome to ANANT WEB PORTAL - a comprehensive Affiliate Network platform with e-commerce capabilities. 
    
This platform offers Affiliate Network network with 10x10 matrix structure, e-commerce shop with products and courses, commission wallet system with multiple payment methods, shop builder for creating custom online stores, Magic QR for smart review collection, and digital visiting cards.

System Requirements: You need a modern web browser like Chrome, Firefox, Safari, or Edge, stable internet connection, and a mobile device or desktop computer.`
  },
  {
    id: 2,
    title: 'User Registration and Login',
    content: `To register on the platform: Click Register on the homepage, enter your personal details including Name, Email, and Phone, create a strong password, enter your sponsor's referral code if you have one, verify your email address, and complete your profile setup.

Login Options include email and password login, remember me option for quick access, and password recovery via email.

For enhanced security, enable Two-Factor Authentication by going to Profile, then Security Settings, enable Two-Factor Authentication, scan QR code with authenticator app, and enter verification code to confirm.`
  },
  {
    id: 3,
    title: 'Dashboard Overview',
    content: `Your dashboard displays activation status banner, wallet balances including Cash, Commission, and Shopping wallets, quick transfer functionality, recent orders and transactions, team statistics and referral links, and featured content and announcements.

Quick Links help you access all features quickly including My Wallets to view and manage all wallet balances, My Orders to track purchase history, My Products to access downloaded products, My Courses to continue learning, My Shops to manage your online stores, Magic QR to create smart QR codes, Commissions to track Affiliate Network earnings, and Team Report to view downline members.`
  },
  {
    id: 4,
    title: 'Wallet System',
    content: `The platform uses three separate wallets: Cash Wallet for adding funds, purchases, and withdrawals, Commission Wallet which receives Affiliate Network commissions from referrals, and Shopping Wallet which is a special wallet for shopping discounts.

To add funds to your Cash Wallet: Go to My Wallets then Add Funds, enter the amount you wish to add, scan the UPI QR code or use bank transfer, enter transaction reference number, upload payment screenshot, and submit request for admin approval.

You can transfer funds between wallets or to other users. Commission to Cash converts commissions to withdrawable cash. User to User sends funds to team members. Quick Transfer enables fast transfer using member ID.

For withdrawals from Cash Wallet: Minimum withdrawal is 500 Rupees, maximum withdrawal is 50,000 Rupees per day, processing time is 24 to 48 hours, and you need to add bank details in Profile then Payment Settings.`
  },
  {
    id: 5,
    title: 'Freedom Activation',
    content: `Freedom Activation unlocks Affiliate Network features and allows you to earn commissions from referrals, access premium products, build your network, and participate in rank competitions.

To activate your account: Go to Activation Packs page, select your preferred activation pack, choose ONE product to unlock - this is important, select payment method which can be Manual Payment via UPI or QR, 100% Cash Wallet, or 50% Cash plus 50% Commission, submit activation request, and wait for admin approval for manual payments.

When you activate, you must select ONE product to unlock. The selected product becomes accessible after approval. You can view unlocked products in Dashboard under My Unlocked Products section, and download or stream product files including videos, PDFs, and audio.`
  },
  {
    id: 6,
    title: 'Products and Courses',
    content: `The Products page offers digital products like software, ebooks, and templates, training materials, business tools, filter by category, and search functionality.

To purchase a product: Browse products and select one, add to cart, proceed to checkout, pay using wallet or online payment, and access downloads in My Products.

Products unlocked through activation can be viewed in Dashboard under My Unlocked Products section. You can stream videos directly in browser, listen to audio files, download PDF documents, and access all product files without additional payment.

Online Courses provide access to learning content including video tutorials, PDF guides, module-based learning, progress tracking, and certificate on completion.`
  },
  {
    id: 7,
    title: 'Affiliate Network and Commission System',
    content: `The Affiliate Network system uses a 10-level deep, 10-wide matrix. Direct referrals are Level 1, their referrals are Level 2, and so on up to Level 10.

Commission Types include Direct Referral Commission when your direct referral activates, Level Commission from all 10 levels of your network, Product Commission from product sales in your network, and Rank Bonus as additional rewards based on your rank.

Standard commission structure: Level 1 is 10% of activation amount, Levels 2 to 5 are 5% each, Levels 6 to 10 are 3% each. Note that rates may vary based on your rank and promotions.

To view your team information: My Matrix shows visual tree view of your network, Team Report shows detailed list with contact info, level-wise breakdown of members, and active versus inactive member counts.`
  },
  {
    id: 8,
    title: 'Shop Builder',
    content: `To build your own online store: Go to My Shops, click Create New Shop, enter shop name and description, customize colors and branding, add your products, set up payment methods, and publish your shop.

Shop Customization includes logo and banner images, color themes and fonts, custom sections and pages, social media links, and contact information.

For Product Management in your shop: Upload product images and files including JPEG, GIF, MP4, MP3, and PDF, set pricing and discounts, manage inventory, and process orders.

Payment Integration allows you to accept payments via Razorpay for UPI, Cards, and Net Banking, Stripe for international cards, manual payment with QR code, and configure test or live mode in settings.`
  },
  {
    id: 9,
    title: 'Magic QR System',
    content: `Magic QR is a smart review collection system. Create QR codes for your business, customers scan and leave reviews, positive reviews redirect to Google or social media, negative reviews come to you privately, and build your online reputation efficiently.

To create a Magic QR: Go to Magic QR page, click Create Magic QR, enter business details, set Google review link, customize feedback questions, and download and print QR code.

QR Code Limits: Free users get 1 Magic QR code, activated users get up to 10 Magic QR codes, and premium users get unlimited QR codes.`
  },
  {
    id: 10,
    title: 'Admin Panel Guide',
    content: `The admin panel provides user management, product and course management, order processing, commission configuration, payment settings, and site customization.

For Managing Users, admin can view all registered users, activate or deactivate accounts, adjust wallet balances, reset passwords, and view user activity logs.

Product management features include adding new products with file uploads, supported files like JPEG, GIF, PNG, MP4, MP3, and PDF with no file size limit, multiple files per product, editing existing products, and managing categories.

For Activation Pack Management: Configure activation packs, set pack pricing and duration, add pack files and resources, configure unlockable products, approve pending activations, and track activation statistics.`
  },
  {
    id: 11,
    title: 'Payment Settings',
    content: `Admin can configure payment gateways including Razorpay with Key ID and Secret, Stripe with Publishable and Secret Key, test mode versus live mode toggle, and default payment method selection.

Test mode allows safe testing without real transactions, using test API keys, verifying payment flow, and switching to live when ready.

For UPI and QR Payments manual setup: Upload UPI QR code, set UPI ID, review payment screenshots, and approve or reject fund requests.`
  },
  {
    id: 12,
    title: 'PWA and Push Notifications',
    content: `To install ANANT WEB PORTAL on your device as a PWA: Open the website in Chrome or Safari, look for Add to Home Screen prompt, click Install or Add, access like a native app, and it works offline for cached content.

To enable push notifications: Go to Notifications page, click Enable Push Notifications, allow browser permission, and receive alerts for new commissions, team activity, order updates, and announcements.

Admins can send broadcast messages to all users, targeted notifications to specific users, important announcements, and promotional messages.`
  },
  {
    id: 13,
    title: 'FAQ and Support',
    content: `Frequently Asked Questions:

How do I reset my password? Click Forgot Password on login page, enter your email, and follow the reset link.

How long does withdrawal take? Withdrawals are processed within 24 to 48 hours on business days.

Can I change my sponsor? Sponsor cannot be changed after registration. Contact admin for special cases.

How do I access my unlocked product? Go to Dashboard and find My Unlocked Products section to view and download.

What file types can I upload? Supported files include JPEG, GIF, PNG for images, MP4 for video, MP3 for audio, and PDF for documents.

For assistance, use WhatsApp through the chat widget on the website, or contact through the support form via email. Response time is 24 to 48 hours.`
  },
  {
    id: 14,
    title: 'Marketing Tips - Vyapar Vikas',
    content: `Marketing Tips for Business Growth:

Referral Marketing: Har din kam se kam 5 logon se baat karein. WhatsApp groups mein active rahein. Personal experience share karein. Referral link har jagah daalein including bio, status, signature. Follow-up ke liye reminder set karein.

Social Media Strategy: Daily 2-3 posts karein, morning and evening best time hai. Success stories share karein. Before and After results dikhayein. Video content banayein, Reels perform better. Relevant hashtags use karein.

WhatsApp Marketing: Professional WhatsApp Business profile banayein. Catalog feature mein products add karein. Status daily update karein. Broadcast lists use karein, groups se better hai. Quick replies set karein. WhatsApp Link Generator tool use karein!

P2P Deals Marketing: High-quality photos upload karein. Detailed descriptions likhein. Competitive pricing rakhein. Quick response dein inquiries par. Positive reviews collect karein. PREMIUM pass se priority listing payein.

Auction Strategy: Budget pehle se decide karein. Last 2 minutes mein strategic bid karein. Anti-snipe timer ka fayda uthayein. High-demand items identify karein. Patience rakhein, impulse bids se bachein.

Franchise Tips for Captains: Apne area mein physically meet karein. Local events organize karein. Team trainings conduct karein. Success stories document karein. Monthly targets set karein. Top performers ko recognize karein.

Magic QR Success: Counter par QR code display karein. Bill ke saath QR code print karein. Staff ko train karein politely request karne ke liye. Review dein aur discount payein offer dein. Negative feedback ko private mein handle karein.

Daily Routine for Success: Morning 9 to 11 AM - New prospects ko message karein, social media posts schedule karein, team updates check karein. Afternoon 2 to 5 PM - Follow-ups karein, one-on-one meetings schedule karein, training content consume karein. Evening 7 to 9 PM - Team calls join karein, results track karein, next day plan karein.

Golden Rule: Consistency is key - daily activity zaroori hai!

Thank you for using ANANT WEB PORTAL!`
  }
];

const AudioGuide = () => {
  const { user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('hi-IN');
  const [showLanguages, setShowLanguages] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [rate, setRate] = useState(1);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [copied, setCopied] = useState(false);
  const utteranceRef = useRef(null);
  const synthRef = useRef(null);

  // Build referral link for sharing
  const buildShareLink = (pdfType = 'hindi') => {
    const baseUrl = window.location.origin;
    const referralId = user?.username || user?.id || '';
    const pdfPath = pdfType === 'hindi' ? '/System_Guide_Hindi.pdf' : '/System_Guide.pdf';
    return referralId 
      ? `${baseUrl}/register?ref=${referralId}&guide=${pdfType}` 
      : `${baseUrl}${pdfPath}`;
  };

  const shareText = `ANANT WEB PORTAL - Complete System Guide with Marketing Tips! Learn everything about Affiliate Network, P2P Deals, Auctions, Franchise & more. Download FREE PDF Guide:`;

  const handleCopyLink = async () => {
    const link = buildShareLink('hindi');
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${link}`);
      setCopied(true);
      toast.success('Guide link copied with your referral ID!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleShareWhatsApp = () => {
    const link = buildShareLink('hindi');
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${link}`)}`;
    window.open(url, '_blank');
  };

  const handleShareTelegram = () => {
    const link = buildShareLink('hindi');
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    // Initialize speech synthesis
    synthRef.current = window.speechSynthesis;
    
    // Load voices
    const loadVoices = () => {
      const voices = synthRef.current.getVoices();
      setAvailableVoices(voices);
    };
    
    loadVoices();
    synthRef.current.onvoiceschanged = loadVoices;
    
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const getVoiceForLanguage = (langCode) => {
    // Find a voice that matches the language
    const voice = availableVoices.find(v => v.lang === langCode) ||
                  availableVoices.find(v => v.lang.startsWith(langCode.split('-')[0])) ||
                  availableVoices[0];
    return voice;
  };

  const speak = (text, chapterIndex) => {
    if (!synthRef.current) {
      toast.error('Text-to-speech not supported in this browser');
      return;
    }

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getVoiceForLanguage(selectedLanguage);
    
    if (voice) {
      utterance.voice = voice;
    }
    utterance.lang = selectedLanguage;
    utterance.rate = rate;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsPlaying(true);
      setCurrentChapter(chapterIndex);
    };

    utterance.onend = () => {
      // Auto-play next chapter
      if (chapterIndex < GUIDE_CHAPTERS.length - 1) {
        setTimeout(() => {
          speak(GUIDE_CHAPTERS[chapterIndex + 1].content, chapterIndex + 1);
        }, 1000);
      } else {
        setIsPlaying(false);
        toast.success('Audio guide completed!');
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      setIsPlaying(false);
      toast.error('Speech synthesis error');
    };

    utteranceRef.current = utterance;
    synthRef.current.speak(utterance);
  };

  const handlePlay = () => {
    if (isPlaying) {
      synthRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (synthRef.current?.paused) {
        synthRef.current.resume();
        setIsPlaying(true);
      } else {
        speak(GUIDE_CHAPTERS[currentChapter].content, currentChapter);
      }
    }
  };

  const handleStop = () => {
    synthRef.current?.cancel();
    setIsPlaying(false);
    setCurrentChapter(0);
  };

  const handlePrevious = () => {
    synthRef.current?.cancel();
    const prevChapter = Math.max(0, currentChapter - 1);
    setCurrentChapter(prevChapter);
    if (isPlaying) {
      speak(GUIDE_CHAPTERS[prevChapter].content, prevChapter);
    }
  };

  const handleNext = () => {
    synthRef.current?.cancel();
    const nextChapter = Math.min(GUIDE_CHAPTERS.length - 1, currentChapter + 1);
    setCurrentChapter(nextChapter);
    if (isPlaying) {
      speak(GUIDE_CHAPTERS[nextChapter].content, nextChapter);
    }
  };

  const playChapter = (index) => {
    synthRef.current?.cancel();
    speak(GUIDE_CHAPTERS[index].content, index);
  };

  const getCurrentLanguageName = () => {
    return LANGUAGES.find(l => l.code === selectedLanguage)?.name || 'Hindi';
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="audio-guide-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary/20 rounded-full mb-4">
            <Volume2 className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">Audio Guide</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            🎧 System Guide Audiobook
          </h1>
          <p className="text-muted-foreground text-lg">
            Listen to the complete system guide in your preferred language
          </p>
        </div>

        {/* Language Selector */}
        <div className="glass-card p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Languages className="w-5 h-5 text-primary" />
              <span className="font-medium">Language:</span>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowLanguages(!showLanguages)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                data-testid="language-selector"
              >
                <span>{getCurrentLanguageName()}</span>
                {showLanguages ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showLanguages && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-background border border-white/10 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
                  {/* Indian Languages */}
                  <div className="px-4 py-2 bg-primary/10 sticky top-0">
                    <span className="text-xs font-bold text-primary uppercase">🇮🇳 Indian Languages</span>
                  </div>
                  {LANGUAGES.filter(l => l.region === 'Indian').map((lang) => {
                    const hasVoice = availableVoices.some(v => v.lang === lang.code || v.lang.startsWith(lang.code.split('-')[0]));
                    return (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setSelectedLanguage(lang.code);
                          setShowLanguages(false);
                          if (isPlaying) {
                            synthRef.current?.cancel();
                            setIsPlaying(false);
                          }
                          toast.success(`Language changed to ${lang.name}`);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between ${
                          selectedLanguage === lang.code ? 'bg-primary/20 text-primary' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </div>
                        {hasVoice ? (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded-full flex items-center gap-1">
                            <Mic className="w-3 h-3" /> Available
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-500 rounded-full">Fallback</span>
                        )}
                      </button>
                    );
                  })}
                  
                  {/* International Languages */}
                  <div className="px-4 py-2 bg-secondary/10 sticky top-0">
                    <span className="text-xs font-bold text-secondary uppercase">🌍 International Languages</span>
                  </div>
                  {LANGUAGES.filter(l => l.region === 'International').map((lang) => {
                    const hasVoice = availableVoices.some(v => v.lang === lang.code || v.lang.startsWith(lang.code.split('-')[0]));
                    return (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setSelectedLanguage(lang.code);
                          setShowLanguages(false);
                          if (isPlaying) {
                            synthRef.current?.cancel();
                            setIsPlaying(false);
                          }
                          toast.success(`Language changed to ${lang.name}`);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between ${
                          selectedLanguage === lang.code ? 'bg-primary/20 text-primary' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </div>
                        {hasVoice ? (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded-full flex items-center gap-1">
                            <Mic className="w-3 h-3" /> Available
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-500 rounded-full">Fallback</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Player Controls */}
        <div className="glass-card p-6 mb-6 border-2 border-primary/30">
          {/* Now Playing */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground mb-1">Now Playing</p>
            <h2 className="text-xl font-bold text-primary">
              Chapter {currentChapter + 1}: {GUIDE_CHAPTERS[currentChapter].title}
            </h2>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Chapter {currentChapter + 1}</span>
              <span>{GUIDE_CHAPTERS.length} Chapters</span>
            </div>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((currentChapter + 1) / GUIDE_CHAPTERS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handlePrevious}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              disabled={currentChapter === 0}
              data-testid="prev-chapter"
            >
              <SkipBack className="w-6 h-6" />
            </button>
            
            <button
              onClick={handlePlay}
              className={`p-5 rounded-full transition-all ${
                isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/80'
              }`}
              data-testid="play-pause"
            >
              {isPlaying ? (
                <Pause className="w-8 h-8 text-white" />
              ) : (
                <Play className="w-8 h-8 text-white ml-1" />
              )}
            </button>
            
            <button
              onClick={handleStop}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              data-testid="stop"
            >
              <Square className="w-6 h-6" />
            </button>
            
            <button
              onClick={handleNext}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              disabled={currentChapter === GUIDE_CHAPTERS.length - 1}
              data-testid="next-chapter"
            >
              <SkipForward className="w-6 h-6" />
            </button>
          </div>

          {/* Speed Control */}
          <div className="mt-6 flex items-center justify-center gap-4">
            <span className="text-sm text-muted-foreground">Speed:</span>
            <div className="flex gap-2">
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                <button
                  key={speed}
                  onClick={() => setRate(speed)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    rate === speed ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Download PDF Buttons */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-secondary" />
              <span>Prefer reading? Download the guides</span>
            </div>
            <div className="flex gap-3">
              <a
                href="/System_Guide_Hindi.pdf"
                download="ANANT_Guide_Hindi.pdf"
                className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                data-testid="download-hindi-pdf"
              >
                <Download className="w-4 h-4" />
                Hindi PDF
              </a>
              <a
                href="/System_Guide.pdf"
                download="ANANT_Guide_English.pdf"
                className="flex items-center gap-2 px-4 py-2 bg-secondary/20 text-secondary rounded-lg hover:bg-secondary/30 transition-colors"
                data-testid="download-pdf"
              >
                <Download className="w-4 h-4" />
                English PDF
              </a>
            </div>
          </div>
        </div>

        {/* Share Guide with Referral Link */}
        <div className="glass-card p-6 mb-6 border-2 border-green-500/30 bg-gradient-to-r from-green-500/10 to-primary/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-500/20 rounded-full">
              <Share2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Share Guide & Earn!</h3>
              <p className="text-sm text-muted-foreground">Share this guide with your referral link</p>
            </div>
          </div>
          
          {/* Referral Link Display */}
          {user ? (
            <div className="mb-4 p-3 bg-black/20 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Your Referral Link:</p>
              <p className="text-sm text-primary font-mono truncate">{buildShareLink('hindi')}</p>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-500">Login to share with your referral link and earn commissions!</p>
            </div>
          )}

          {/* Share Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleCopyLink}
              className="flex flex-col items-center gap-2 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all hover:scale-105"
              data-testid="copy-guide-link"
            >
              {copied ? (
                <Check className="w-6 h-6 text-green-500" />
              ) : (
                <Copy className="w-6 h-6 text-primary" />
              )}
              <span className="text-xs">{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
            
            <button
              onClick={handleShareWhatsApp}
              className="flex flex-col items-center gap-2 p-3 bg-green-500/20 hover:bg-green-500/30 rounded-xl transition-all hover:scale-105"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-6 h-6 text-green-500" />
              <span className="text-xs">WhatsApp</span>
            </button>
            
            <button
              onClick={handleShareTelegram}
              className="flex flex-col items-center gap-2 p-3 bg-blue-500/20 hover:bg-blue-500/30 rounded-xl transition-all hover:scale-105"
              data-testid="share-telegram"
            >
              <Send className="w-6 h-6 text-blue-400" />
              <span className="text-xs">Telegram</span>
            </button>
          </div>

          {/* Marketing Tip */}
          <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
            <p className="text-xs text-primary">
              <strong>Marketing Tip:</strong> Share this guide on WhatsApp status daily! When people register using your link, you earn commission on their activations.
            </p>
          </div>
        </div>

        {/* Chapters List */}
        <div className="glass-card p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            All Chapters ({GUIDE_CHAPTERS.length})
          </h3>
          
          <div className="space-y-2">
            {GUIDE_CHAPTERS.map((chapter, index) => (
              <div 
                key={chapter.id}
                className={`rounded-xl overflow-hidden transition-all ${
                  currentChapter === index ? 'border-2 border-primary' : 'border border-white/10'
                }`}
              >
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 ${
                    currentChapter === index && isPlaying ? 'bg-primary/10' : ''
                  }`}
                  onClick={() => setExpandedChapter(expandedChapter === index ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      currentChapter === index ? 'bg-primary text-white' : 'bg-white/10'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium">{chapter.title}</span>
                    {currentChapter === index && isPlaying && (
                      <span className="flex items-center gap-1 text-xs text-primary">
                        <Volume2 className="w-3 h-3 animate-pulse" />
                        Playing
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playChapter(index);
                      }}
                      className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                      data-testid={`play-chapter-${index}`}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    {expandedChapter === index ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {expandedChapter === index && (
                  <div className="px-4 pb-4 pt-2 text-sm text-muted-foreground border-t border-white/10">
                    <p className="whitespace-pre-line">{chapter.content.substring(0, 300)}...</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Browser Support Note */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>🎧 Uses browser built-in text-to-speech. Voice quality may vary by browser and device.</p>
          <p className="mt-1">Best experience on Chrome or Edge with Indian language voices installed.</p>
        </div>
      </div>
    </div>
  );
};

export default AudioGuide;
